// GitOps Change Specs - Strict Schema for LLM Output

export const changeSpecSchema = {
  $schema: 'https://vedaya.ai/schemas/change-spec/v1',
  type: 'object',
  required: ['version', 'intent', 'changes', 'metadata'],
  properties: {
    version: { type: 'string', const: '1.0' },
    intent: {
      type: 'object',
      required: ['description', 'justification', 'requestedBy'],
      properties: {
        description: { type: 'string' },
        justification: { type: 'string' },
        requestedBy: { type: 'string' },
        ticketRef: { type: 'string' },
        incidentRef: { type: 'string' }
      }
    },
    changes: {
      type: 'array',
      items: {
        type: 'object',
        required: ['moduleRef', 'action', 'parameters'],
        properties: {
          moduleRef: { type: 'string', pattern: '^[a-z]+-[a-z-]+$' },
          moduleVersion: { type: 'string' },
          action: { type: 'string', enum: ['create', 'update', 'delete'] },
          parameters: { type: 'object' },
          dependencies: { type: 'array', items: { type: 'string' } }
        }
      }
    },
    metadata: {
      type: 'object',
      required: ['environment', 'riskLevel'],
      properties: {
        environment: { type: 'string', enum: ['dev', 'staging', 'prod'] },
        riskLevel: { type: 'string', enum: ['low', 'medium', 'high', 'critical'] },
        requiresMaintenanceWindow: { type: 'boolean' },
        estimatedDuration: { type: 'string' },
        rollbackStrategy: { type: 'string' }
      }
    }
  }
};

export const changeSpecs = [
  {
    id: 'CS-2026-0087',
    version: '1.0',
    intent: {
      description: 'Scale Redis cluster to handle increased traffic',
      justification: 'Redis latency alert triggered - current capacity insufficient',
      requestedBy: 'Platform Team',
      incidentRef: 'INC-2026-0142'
    },
    changes: [
      {
        moduleRef: 'tf-aws-elasticache-redis',
        moduleVersion: '1.5.2',
        action: 'update',
        parameters: {
          cluster_id: 'redis-cache-prod-3',
          node_type: 'cache.r6g.xlarge',
          num_cache_nodes: 4,
          engine_version: '7.0'
        }
      }
    ],
    metadata: {
      environment: 'prod',
      riskLevel: 'medium',
      requiresMaintenanceWindow: false,
      estimatedDuration: '15 minutes',
      rollbackStrategy: 'Revert to previous node count via terraform apply'
    },
    status: 'pending_approval',
    createdAt: '2026-01-11T10:30:00Z',
    createdBy: 'system (incident response)'
  },
  {
    id: 'CS-2026-0086',
    version: '1.0',
    intent: {
      description: 'Offboard Alex Rodriguez from all systems',
      justification: 'Employee termination per HR request',
      requestedBy: 'HR System (Workday)',
      ticketRef: 'HR-2026-1542'
    },
    changes: [
      {
        moduleRef: 'config-okta-user',
        moduleVersion: '1.2.0',
        action: 'update',
        parameters: {
          email: 'alex.rodriguez@company.com',
          status: 'SUSPENDED'
        }
      },
      {
        moduleRef: 'config-github-team',
        moduleVersion: '1.0.5',
        action: 'update',
        parameters: {
          team_name: 'identity-team',
          members: ['remove:alex-rodriguez']
        }
      },
      {
        moduleRef: 'workflow-user-offboarding',
        moduleVersion: '2.0.0',
        action: 'create',
        parameters: {
          user_email: 'alex.rodriguez@company.com',
          transfer_owner: 'lisa.park@company.com',
          retention_days: 30,
          notify_manager: true
        }
      }
    ],
    metadata: {
      environment: 'prod',
      riskLevel: 'high',
      requiresMaintenanceWindow: false,
      estimatedDuration: '20 minutes',
      rollbackStrategy: 'Re-enable user accounts and restore group memberships'
    },
    status: 'merged',
    createdAt: '2026-01-10T14:00:00Z',
    createdBy: 'HR System'
  },
  {
    id: 'CS-2026-0085',
    version: '1.0',
    intent: {
      description: 'Deploy new staging database for analytics team',
      justification: 'New analytics workload requires isolated database',
      requestedBy: 'Data Team',
      ticketRef: 'JIRA-DATA-1234'
    },
    changes: [
      {
        moduleRef: 'tf-aws-rds-postgres',
        moduleVersion: '2.8.0',
        action: 'create',
        parameters: {
          identifier: 'analytics-staging-db',
          engine_version: '16',
          instance_class: 'db.r6g.large',
          allocated_storage: 100,
          multi_az: false,
          encryption_enabled: true
        }
      },
      {
        moduleRef: 'policy-security-baseline',
        moduleVersion: '2.1.0',
        action: 'create',
        parameters: {
          enforce_encryption: true,
          require_mfa: true,
          audit_retention_days: 90
        }
      }
    ],
    metadata: {
      environment: 'staging',
      riskLevel: 'low',
      requiresMaintenanceWindow: false,
      estimatedDuration: '30 minutes',
      rollbackStrategy: 'Destroy RDS instance via terraform destroy'
    },
    status: 'applied',
    createdAt: '2026-01-09T09:00:00Z',
    createdBy: 'Sarah Chen'
  },
  {
    id: 'CS-2026-0084',
    version: '1.0',
    intent: {
      description: 'Quarantine device LT-1042 due to missing EDR',
      justification: 'Security policy violation - EDR agent not detected',
      requestedBy: 'Security Team',
      incidentRef: 'SEC-2026-0089'
    },
    changes: [
      {
        moduleRef: 'workflow-incident-quarantine',
        moduleVersion: '1.1.0',
        action: 'create',
        parameters: {
          device_id: 'LT-1042',
          reason: 'EDR agent not detected - policy violation',
          network_isolate: true,
          revoke_tokens: true
        }
      }
    ],
    metadata: {
      environment: 'prod',
      riskLevel: 'critical',
      requiresMaintenanceWindow: false,
      estimatedDuration: '5 minutes',
      rollbackStrategy: 'Release network isolation and restore tokens'
    },
    status: 'applied',
    createdAt: '2026-01-07T16:40:00Z',
    createdBy: 'system (policy engine)'
  }
];

export const invalidChangeSpecExamples = [
  {
    error: 'INVALID_MODULE_REF',
    message: 'Module "custom-script-runner" is not in the approved catalog',
    suggestion: 'Use an approved module from the catalog or request module addition'
  },
  {
    error: 'INVALID_PARAMETER',
    message: 'Parameter "instance_class" value "db.r6g.16xlarge" exceeds allowed limits',
    suggestion: 'Choose from allowed instance classes defined in module parameters'
  },
  {
    error: 'MISSING_REQUIRED_FIELD',
    message: 'Required field "metadata.environment" is missing',
    suggestion: 'Specify target environment: dev, staging, or prod'
  },
  {
    error: 'SCHEMA_VIOLATION',
    message: 'Freeform script execution is not allowed in Change Spec',
    suggestion: 'All changes must reference approved catalog modules'
  }
];